import React, {useReducer, useState} from 'react'
import {makeStyles, Table, TableBody, TableCell, TableHead, TableRow} from '@material-ui/core';
import Tr from 'translations/Translations';
import WorkableIconDeleteButton from 'components/button/WorkableIconDeleteButton';
import DownloadHistoryPopup, {createDownloadHistoryFile} from "components/popup/DownloadHistoryPopup";
import Confirm from "components/popup/confirm/Confirm";
import EditDescriptionPopup, {createEditDescriptionFile} from "components/popup/EditDescriptionPopup";
import BoldTypography from "components/BoldTypography";
import PropTypes from 'prop-types';
import WorkableIconPublishButton from "components/button/WorkableIconPublishButton";
import ChangeRepoVisibilityPopup from "./ChangeRepoVisibilityPopup";
import RepoService from "services/RepoService";
import RepoFileTableRow from "./RepoFileTableRow";
import iconWorksReducer from "libs/iconWorksReducer";
import WorkableIconEjectButton from "../../../button/WorkableIconEjectButton";

const useStyle = makeStyles(theme => ({
    lpColumn: theme.components.table.lpColumn,
    buttonColumn: theme.components.table.buttonColumn,
    dateColumn: theme.components.table.dateColumn,
    typeColumn: {
        width: 180
    },
    visibilityColumn: {
        width: 120
    },
    sizeColumn: {
        width: 80
    }
}));

function RepoFileTable(
    {
        demandId,
        isPublished,
        folder,
        disabled,
        showShare,
        onMoveToSiwz
    }) {
    const classes = useStyle();
    const [downloadHistory, setDownloadHistory] = useState(null);
    const [openConfirmDeleteAll, setOpenConfirmDeleteAll] = useState(false);
    const [editDescriptionFile, setEditDescriptionFile] = useState(null);
    const [repoVisibility, setRepoVisibility] = useState(null);

    const [iconWorks, dispatchIconWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        delete: false,
    });

    disabled = disabled || iconWorks.anyWorks;

    const onDeleteAll = () => {
        dispatchIconWorks({type: 'delete', works: true})
        setOpenConfirmDeleteAll(false);
        RepoService.deleteAll(demandId, folder.identity).finally(() => dispatchIconWorks({type: 'delete', works: false}))
    };


    const openDownloadHistory = (row) => {
        setDownloadHistory(createDownloadHistoryFile(row.name, demandId, row.identity));
    };

    const openEditDescription = (row) => {
        setEditDescriptionFile(createEditDescriptionFile(demandId, RepoService.buildIdentity(row.identity, folder.identity),
            row.name, row.description, !row.actions.editDescription));
    };

    const onChangeVisibilityAll = () => {
        const visibilityStatus = folder.files.length > 0 ? folder.files[0].visibilityStatus : null;
        const visibilityEndDate = folder.files.length > 0 ? folder.files[0].visibilityEndDate : null;
        const folderContractors = folder.contractors;
        setRepoVisibility({folderId: folder.identity, folderType: folder.type, name: folder.name, visibilityStatus, visibilityEndDate, folderContractors});
    };

    const onSetRepoVisibility = (row) => {
        setRepoVisibility({
            fileId: row.identity,
            folderId: row.folderId,
            folderType: folder.type,
            name: row.name,
            visibilityStatus: row.visibilityStatus,
            visibilityEndDate: row.visibilityEndDate,
            contractors: row.contractors
        })
    }

    const onSelectFileToMoveToSiwz = (files) => {
        onMoveToSiwz(folder, files);
    }

    return (<>
        {!!folder.files && <div>
            {!folder.files.length ?
                <BoldTypography align='center' variant="h6">{Tr('no_files_in_directory')}</BoldTypography>
                :
                <>

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.lpColumn}>{Tr("column_lp")}</TableCell>
                                <TableCell>{Tr("name")}</TableCell>
                                <TableCell className={classes.buttonColumn}>{Tr("description")}</TableCell>
                                <TableCell className={classes.visibilityColumn}>{Tr("repo_visibility_type")}</TableCell>
                                <TableCell className={classes.buttonColumn}>{Tr("size")}</TableCell>
                                <TableCell className={classes.dateColumn}>{Tr("add_data")}</TableCell>
                                {isPublished && <TableCell className={classes.dateColumn}>{Tr("publish_data")}</TableCell>}
                                <TableCell className={classes.buttonColumn}></TableCell>
                                <TableCell className={classes.buttonColumn}>
                                    <WorkableIconEjectButton
                                        tooltip={Tr('move_all_to_siwz')}
                                        onClick={() => onSelectFileToMoveToSiwz(folder.files.map((doc, index) => ({identity: doc.identity, name: doc.name})))}
                                        hideTooltip={!folder.actions.moveToSiwz}
                                        disabled={disabled || !folder.actions.moveToSiwz}
                                    />
                                </TableCell>
                                {showShare && <TableCell className={classes.buttonColumn}></TableCell>}
                                <TableCell className={classes.buttonColumn}>
                                    <WorkableIconPublishButton
                                        tooltip={Tr('change_visibility_for_all_files')}
                                        onClick={onChangeVisibilityAll}
                                        disabled={disabled || !folder.actions.updateFolderFilesVisibility}
                                        hideTooltip={!folder.actions.updateFolderFilesVisibility}
                                    />
                                </TableCell>
                                {isPublished && <TableCell className={classes.buttonColumn}></TableCell>}
                                <TableCell className={classes.buttonColumn}>
                                    {!!folder.actions.deleteFolderFiles &&
                                        <WorkableIconDeleteButton
                                            onClick={() => setOpenConfirmDeleteAll(true)}
                                            tooltip={Tr('delete_all_unpublished')}
                                            working={iconWorks.delete}
                                            disabled={disabled}
                                        />}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                folder.files.map((doc, index) => (
                                    <RepoFileTableRow
                                        key={doc.identity}
                                        row={doc}
                                        demandId={demandId}
                                        index={index + 1}
                                        isPublished={isPublished}
                                        onEditDescriptionFile={openEditDescription}
                                        onOpenDownloadHistory={openDownloadHistory}
                                        onSetRepoVisibility={onSetRepoVisibility}
                                        showShare={showShare}
                                        onMoveToSiwz={onSelectFileToMoveToSiwz}
                                    />))
                            }
                        </TableBody>
                    </Table>

                    {downloadHistory &&
                        <DownloadHistoryPopup downloadHistoryFile={downloadHistory} onClose={() => setDownloadHistory(null)} service={RepoService}/>
                    }

                    {repoVisibility &&
                        <ChangeRepoVisibilityPopup repoVisibility={repoVisibility} onClose={() => setRepoVisibility(null)} demandId={demandId}/>
                    }

                    {Boolean(editDescriptionFile) &&
                        <EditDescriptionPopup
                            editDescriptionFile={editDescriptionFile}
                            onUpdated={() => setEditDescriptionFile(null)}
                            onClose={() => setEditDescriptionFile(null)}
                            service={RepoService}
                        />
                    }

                    <Confirm
                        open={openConfirmDeleteAll}
                        onConfirm={onDeleteAll}
                        onCancel={() => setOpenConfirmDeleteAll(false)}
                        confirmText="confirm_delete_all"
                    />
                </>
            }
        </div>
        }
    </>)
}

RepoFileTable.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    isPublished: PropTypes.bool.isRequired,
    folder: PropTypes.shape({
        identity: PropTypes.number.isRequired,
        type: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        actions: PropTypes.object.isRequired,
        files: PropTypes.array.isRequired
    }).isRequired,
    disabled: PropTypes.bool,
    showShare: PropTypes.bool.isRequired
};

export default RepoFileTable;